import React from 'react';
import { useField } from 'formik';
import { useSmartFormContext } from '../forms/utils/useSmartFormContext';
import { PermissionHelper } from '../atoms/common/PermissionHelper';
import { RadioGroupAtom, RadioGroupAtomProps } from '../atoms/RadioGroup/RadioGroupAtom';
import { ReadOnly } from '../atoms/ReadOnly/ReadOnly';
import { AtomWrapper } from '../atoms/AtomWrapper/AtomWrapper';
import _isEqual from 'lodash-es/isEqual';

export interface RadioGroupProps<OptionValue, TargetModel extends any = OptionValue>
  extends RadioGroupAtomProps<OptionValue, TargetModel> {
  className?: string;
}

export function RadioGroup<T, TargetModel extends any = T>({ className, ...props }: RadioGroupProps<T, TargetModel>) {
  const { readonly, getFieldPermissions, permissionDebugger } = useSmartFormContext();
  const [input, meta] = useField<TargetModel | undefined | null>(props.name || 'INVALID');
  const permissions = getFieldPermissions(props.name);
  const inputValue = props.mapInputValue ? props.mapInputValue(input.value ?? undefined) : input.value;

  if (readonly) {
    return (
      <AtomWrapper name={props.name}>
        <ReadOnly
          type={props.type || 'text'}
          infoText={props.infoText}
          label={props.label}
          value={getLabel(inputValue, props.values)}
          className={className}
          error={!!meta.error}
          permission={permissions}
        />
      </AtomWrapper>
    );
  }

  return (
    <div>
      <RadioGroupAtom error={!!meta.error} errors={meta.error} {...props} />
      {permissionDebugger && <PermissionHelper permission={permissions} />}
    </div>
  );
}

function getLabel<T>(value: T | undefined | null, values: { label: string; value: T }[]): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return values.find((it) => it.value === value)?.label ?? value;
  }
  return values.find((it) => _isEqual(it.value, value))?.label ?? value?.toString();
}
